import {
  AbsoluteFill,
  Sequence,
  OffthreadVideo,
  Audio,
  useCurrentFrame,
  interpolate,
} from "remotion";
import React from "react";
import { z } from "zod";
import { toHttpCdn } from "../utils";

// ===== Schema 定义 =====
export const VideoItemSchema = z.object({
  url: z.string().describe("视频 URL"),
  duration: z.number().describe("视频时长（秒）"),
  startFrom: z.number().default(0).describe("视频开始播放的时间点（秒），用于截取精彩片段"),
});

export const HighlightVideoV4PropsSchema = z.object({
  videos: z.array(VideoItemSchema).describe("视频列表，按顺序播放"),
  audioUrl: z.string().optional().describe("背景音乐 URL"),
  audioStartFrom: z.number().default(0).describe("音频起始时间（秒）"),
  audioVolume: z.number().default(0.7).describe("音频音量 (0-1)"),
  // 视频放大倍数（用于裁剪左右边缘）
  videoScale: z.number().default(1.6).describe("视频放大倍数，>1 会裁剪左右"),
  // 卡点转场：指定音乐节拍时间点（相对于 audioStartFrom 的偏移）
  // 例如：[0, 3.5, 7, 10.5] 表示在这些时间点切换视频
  beatTimestamps: z.array(z.number()).optional().describe("音乐节拍时间点数组（秒），用于卡点转场"),
  // 自动节拍对齐：每个视频完整播放后，切换点自动对齐到最近的节拍
  autoBeatAlign: z.boolean().default(false).describe("是否自动将切换点对齐到节拍"),
  bpm: z.number().default(99.4).describe("音乐 BPM，用于自动节拍对齐"),
  // 首尾呼应：在结尾再播放第一个视频的另一部分，让循环更平滑
  loopBack: z.boolean().default(false).describe("是否在结尾回到第一个视频"),
  loopBackStartFrom: z.number().default(0).describe("首尾呼应时，结尾视频从第几秒开始"),
  // 高光预览：将视频最后 x 秒放到开头，慢镜头 + 放大播放
  previewEnabled: z.boolean().default(false).describe("是否启用高光预览"),
  previewDuration: z.number().default(3).describe("预览片段的原始时长（秒），从视频末尾截取"),
  previewSlowRate: z.number().default(0.5).describe("慢放倍率，0.5 表示 2 倍慢放"),
  previewZoom: z.number().default(1.8).describe("放大倍数"),
  previewFocusX: z.number().default(50).describe("聚焦点 X 位置（百分比，50 为中间）"),
  previewFocusY: z.number().default(30).describe("聚焦点 Y 位置（百分比，30 为上部）"),
  title: z.string().default("DOTA2").describe("上方标题"),
  subtitle: z.string().default("超神集锦").describe("上方副标题"),
  brandName: z.string().default("@超神时刻").describe("下方品牌名"),
  brandSubtitle: z.string().default("GODLIKE MOMENTS").describe("下方品牌副标题"),
});

export type HighlightVideoV4Props = z.infer<typeof HighlightVideoV4PropsSchema>;

// ===== 常量 =====
const FPS = 30;
const TRANSITION_DURATION = 15;

// 画布尺寸
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1920;

// 视频区域高度
const VIDEO_HEIGHT = 1400;
const TOP_BANNER_HEIGHT = (CANVAS_HEIGHT - VIDEO_HEIGHT) / 2; // 360
// 蒙层延伸像素，覆盖黑边
const OVERLAY_EXTEND = 5;

// ===== 上方品牌区 =====
const TopBanner: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  const frame = useCurrentFrame();

  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.5, 1, 0.5]);
  const particleOffset = (frame * 2) % 200;
  const floatY = interpolate(frame % 90, [0, 45, 90], [0, -5, 0]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT + OVERLAY_EXTEND,
        background: "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 动态背景线条 */}
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: `${(i * 25 + particleOffset) % 120 - 20}%`,
            width: 2,
            height: "100%",
            background: `linear-gradient(180deg, transparent, rgba(255,215,0,${0.1 + i * 0.05}), transparent)`,
            transform: "skewX(-20deg)",
          }}
        />
      ))}

      {/* 标题 */}
      <div style={{ transform: `translateY(${floatY}px)`, textAlign: "center" }}>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 130,
            fontWeight: "bold",
            color: "#FF4444",
            textShadow: `
              0 0 40px rgba(255,68,68,${glowIntensity}),
              0 0 80px rgba(255,68,68,${glowIntensity * 0.5}),
              0 0 120px rgba(255,68,68,${glowIntensity * 0.3}),
              4px 4px 0 #000
            `,
            letterSpacing: 15,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 90,
            color: "#FFD700",
            marginTop: 20,
            textShadow: `0 0 30px rgba(255,215,0,${glowIntensity})`,
            letterSpacing: 12,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* 底部装饰线 */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,68,68,${glowIntensity}) 20%, 
            rgba(255,215,0,${glowIntensity}) 50%, 
            rgba(255,68,68,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />
    </div>
  );
};

// ===== 下方品牌区 =====
const BottomBanner: React.FC<{ brandName: string; brandSubtitle: string }> = ({
  brandName,
  brandSubtitle,
}) => {
  const frame = useCurrentFrame();

  const breathe = interpolate(frame % 120, [0, 60, 120], [1, 1.05, 1]);
  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.6, 1, 0.6]);

  // 浮动粒子
  const particles = [...Array(8)].map((_, i) => {
    const speed = 1 + (i % 3) * 0.5;
    const offset = (frame * speed + i * 45) % 360;
    const x = 50 + Math.sin((offset * Math.PI) / 180) * 40;
    const y = 50 + Math.cos((offset * Math.PI) / 180) * 25;
    const size = 3 + (i % 3) * 2;
    const opacity = 0.3 + (i % 4) * 0.15;
    return { x, y, size, opacity };
  });

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT + OVERLAY_EXTEND,
        background: "linear-gradient(0deg, #0a0a0a 0%, #1a1a2e 100%)",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 顶部装饰线 */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,215,0,${glowIntensity}) 20%, 
            rgba(255,100,100,${glowIntensity}) 50%, 
            rgba(255,215,0,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />

      {/* 浮动粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: i % 2 === 0 ? "#FFD700" : "#FF6347",
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 2}px ${i % 2 === 0 ? "#FFD700" : "#FF6347"}`,
          }}
        />
      ))}

      {/* 主标题 */}
      <div style={{ transform: `scale(${breathe})`, textAlign: "center", zIndex: 10 }}>
        <span
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 90,
            fontWeight: "bold",
            background: "linear-gradient(90deg, #FFD700, #FFA500, #FF6347)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: `drop-shadow(0 0 20px rgba(255,215,0,${glowIntensity}))`,
            letterSpacing: 4,
          }}
        >
          {brandName}
        </span>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 36,
            color: "rgba(255,255,255,0.7)",
            marginTop: 25,
            letterSpacing: 12,
          }}
        >
          {brandSubtitle}
        </div>
      </div>
    </div>
  );
};

// ===== 转场效果类型 =====
type TransitionType = "scale" | "flash" | "none";

// ===== 闪白转场效果 =====
const FlashTransition: React.FC<{ durationFrames: number }> = ({ durationFrames }) => {
  const frame = useCurrentFrame();
  
  const opacity = interpolate(
    frame,
    [0, durationFrames * 0.3, durationFrames],
    [1, 0.8, 0],
    { extrapolateRight: "clamp" }
  );

  if (frame >= durationFrames) return null;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "white",
        opacity,
        zIndex: 100,
      }}
    />
  );
};

// ===== 全屏单视频组件（带品牌区） =====
const FullScreenVideo: React.FC<{
  src: string;
  startFrom?: number; // 视频开始时间（秒）
  title: string;
  subtitle: string;
  brandName: string;
  brandSubtitle: string;
  showFlash?: boolean; // 是否显示闪白转场
  playbackRate?: number; // 播放速率
  zoom?: number; // 放大倍数
  zoomAnimated?: boolean; // 是否启用 zoom 动画
  zoomDurationFrames?: number; // zoom 动画时长（帧）
  focusX?: number; // 聚焦点 X（百分比）
  focusY?: number; // 聚焦点 Y（百分比）
  videoScale?: number; // 视频基础放大倍数
}> = ({ 
  src, 
  startFrom = 0, 
  title, 
  subtitle, 
  brandName, 
  brandSubtitle, 
  showFlash = false,
  playbackRate = 1,
  zoom = 1,
  zoomAnimated = false,
  zoomDurationFrames = 90, // 默认 3 秒
  focusX = 50,
  focusY = 50,
  videoScale = 1,
}) => {
  const frame = useCurrentFrame();

  const scale = interpolate(frame, [0, TRANSITION_DURATION], [1.05, 1], {
    extrapolateRight: "clamp",
  });

  // 如果启用 zoom 动画，从 1 渐变到目标 zoom
  const currentZoom = zoomAnimated
    ? interpolate(frame, [0, zoomDurationFrames], [1, zoom], { extrapolateRight: "clamp" })
    : zoom;

  // 计算放大后的偏移量，使聚焦点居中
  const translateX = (50 - focusX) * (currentZoom - 1) / currentZoom * 100 / 50;
  const translateY = (50 - focusY) * (currentZoom - 1) / currentZoom * 100 / 50;

  // 总放大倍数 = 动态缩放 * zoom
  const totalScale = scale * currentZoom;

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 闪白转场 */}
      {showFlash && <FlashTransition durationFrames={8} />}

      {/* 上方品牌区 */}
      <TopBanner title={title} subtitle={subtitle} />

      {/* 视频区域（居中） */}
      <div
        style={{
          position: "absolute",
          top: TOP_BANNER_HEIGHT,
          left: 0,
          width: "100%",
          height: VIDEO_HEIGHT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        <OffthreadVideo
          src={toHttpCdn(src)}
          startFrom={Math.floor(startFrom * FPS)}
          playbackRate={playbackRate}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${totalScale}) translate(${translateX}%, ${translateY}%)`,
          }}
        />
      </div>

      {/* 下方品牌区 */}
      <BottomBanner brandName={brandName} brandSubtitle={brandSubtitle} />
    </AbsoluteFill>
  );
};

// ===== 自动计算节拍对齐的时间点 =====
export const calculateAutoBeatTimestampsV4 = (
  videos: z.infer<typeof VideoItemSchema>[],
  bpm: number = 99.4
): number[] => {
  const beatInterval = 60 / bpm;
  let cumulative = 0;
  const timestamps = [0];
  
  videos.forEach((video) => {
    cumulative += video.duration;
    // 对齐到最近的节拍
    const nearestBeat = Math.round(cumulative / beatInterval) * beatInterval;
    timestamps.push(parseFloat(nearestBeat.toFixed(2)));
  });
  
  return timestamps;
};

// ===== 计算视频帧信息 =====
export const calculateVideoFramesV4 = (
  videos: z.infer<typeof VideoItemSchema>[],
  beatTimestamps?: number[],
  autoBeatAlign?: boolean,
  bpm?: number,
  previewEnabled?: boolean,
  previewDuration?: number,
  previewSlowRate?: number
) => {
  // 计算预览片段的帧数（如果启用）
  let previewFrames = 0;
  if (previewEnabled && previewDuration && previewSlowRate) {
    // 慢放后的实际时长 = 原始时长 / 慢放倍率
    const previewActualDuration = previewDuration / previewSlowRate;
    previewFrames = Math.ceil(previewActualDuration * FPS);
  }

  // 如果启用自动节拍对齐，计算时间点
  const effectiveTimestamps = autoBeatAlign 
    ? calculateAutoBeatTimestampsV4(videos, bpm)
    : beatTimestamps;

  // 如果有节拍时间点，按节拍切换
  if (effectiveTimestamps && effectiveTimestamps.length > 1) {
    const frameInfo = videos.slice(0, effectiveTimestamps.length - 1).map((_, index) => {
      const startTime = effectiveTimestamps[index];
      const endTime = effectiveTimestamps[index + 1];
      const startFrame = Math.floor(startTime * FPS) + previewFrames;
      const durationInFrames = Math.floor((endTime - startTime) * FPS);
      return {
        startFrame,
        durationInFrames,
      };
    });
    const totalFrames = Math.floor(effectiveTimestamps[effectiveTimestamps.length - 1] * FPS) + previewFrames;
    return { frameInfo, totalFrames, previewFrames };
  }

  // 否则按视频原始时长顺序播放
  // 如果启用预览，正片最后 previewDuration 秒不播放（已在预览中展示）
  const trimFrames = previewEnabled && previewDuration ? Math.ceil(previewDuration * FPS) : 0;
  
  let currentFrame = previewFrames;
  const frameInfo = videos.map((video, index) => {
    let frames = Math.ceil(video.duration * FPS);
    // 第一个视频截掉最后 previewDuration 秒
    if (index === 0 && trimFrames > 0) {
      frames = Math.max(0, frames - trimFrames);
    }
    const info = {
      startFrame: currentFrame,
      durationInFrames: frames,
    };
    currentFrame += frames;
    return info;
  });
  return {
    frameInfo,
    totalFrames: currentFrame,
    previewFrames,
  };
};

// ===== 主组件 =====
export const HighlightVideoV4: React.FC<HighlightVideoV4Props> = ({
  videos,
  audioUrl,
  audioStartFrom = 0,
  audioVolume = 0.7,
  videoScale = 1.6,
  beatTimestamps,
  autoBeatAlign = false,
  bpm = 99.4,
  loopBack = false,
  loopBackStartFrom = 0,
  previewEnabled = false,
  previewDuration = 3,
  previewSlowRate = 0.5,
  previewZoom = 1.8,
  previewFocusX = 50,
  previewFocusY = 30,
  title = "DOTA2",
  subtitle = "超神集锦",
  brandName = "@超神时刻",
  brandSubtitle = "GODLIKE MOMENTS",
}) => {
  // 如果启用首尾呼应，在视频列表末尾添加第一个视频
  const finalVideos = loopBack && videos.length > 0
    ? [...videos, { ...videos[0], startFrom: loopBackStartFrom }]
    : videos;
  
  const { frameInfo, previewFrames } = calculateVideoFramesV4(
    finalVideos, 
    beatTimestamps, 
    autoBeatAlign, 
    bpm,
    previewEnabled,
    previewDuration,
    previewSlowRate
  );

  // 计算预览片段的视频起始时间（从第一个视频的末尾截取）
  const firstVideo = videos[0];
  const previewStartTime = firstVideo ? firstVideo.duration - previewDuration : 0;

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 背景音乐 */}
      {audioUrl && (
        <Audio src={toHttpCdn(audioUrl)} startFrom={Math.floor(audioStartFrom * FPS)} volume={audioVolume} />
      )}

      {/* 高光预览片段 - 慢镜头 + 放大聚焦 */}
      {previewEnabled && firstVideo && previewFrames > 0 && (
        <Sequence from={0} durationInFrames={previewFrames}>
          <FullScreenVideo
            src={firstVideo.url}
            startFrom={previewStartTime}
            title={title}
            subtitle={subtitle}
            brandName={brandName}
            brandSubtitle={brandSubtitle}
            showFlash={false}
            playbackRate={previewSlowRate}
            zoom={previewZoom}
            zoomAnimated={true}
            zoomDurationFrames={previewFrames}
            focusX={previewFocusX}
            focusY={previewFocusY}
          />
        </Sequence>
      )}

      {/* 按顺序播放视频 */}
      {finalVideos.slice(0, frameInfo.length).map((video, index) => (
        <Sequence
          key={index}
          from={frameInfo[index].startFrame}
          durationInFrames={frameInfo[index].durationInFrames}
        >
          <FullScreenVideo
            src={video.url}
            startFrom={video.startFrom || 0}
            title={title}
            subtitle={subtitle}
            brandName={brandName}
            brandSubtitle={brandSubtitle}
            showFlash={false}
            videoScale={videoScale}
          />
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

// 导出视频配置供 Root 使用
export const HIGHLIGHT_VIDEO_V4_CONFIG = {
  fps: FPS,
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
};
